import React, { useEffect, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Eye } from 'lucide-react';

import PageHeader from '../../components/layout/PageHeader';
import draftService from '../../services/draft.service';
import { ROUTES } from '../../config/routes.config';

const PAGE_SIZE = 10;

const DraftsPage: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = 'RRMS | Drafts';
  }, []);

  const query = useQuery({
    queryKey: ['drafts', page],
    queryFn: () => draftService.getDrafts({ page, limit: PAGE_SIZE }),
  });

  const rawData = query.data?.data?.data ?? query.data?.data;
  const items = Array.isArray(rawData?.items) ? rawData.items : [];
  const pagination =
    rawData?.pagination ?? {
      total: items.length,
      page,
      limit: PAGE_SIZE,
      totalPages: 1,
      hasNextPage: false,
      hasPrevPage: false,
    };

  return (
    <div>
      <PageHeader
        title="Drafts"
        subtitle="View and submit your saved upload drafts"
      />

      {query.isLoading ? (
        <div className="card p-6">Loading drafts...</div>
      ) : query.isError ? (
        <div className="card p-6">
          <p>{(query.error as any)?.response?.data?.message || 'Failed to fetch drafts.'}</p>
          <button
            type="button"
            className="rounded-md bg-primary px-3 py-2 text-white mt-3"
            onClick={() => query.refetch()}
          >
            Retry
          </button>
        </div>
      ) : items.length === 0 ? (
        <div className="card p-6 text-gray-600">No drafts found.</div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table style={{ minWidth: 900, borderCollapse: 'collapse', fontSize: 'var(--font-size-sm)' }}>
              <thead>
                <tr style={{ background: 'var(--color-primary)', color: 'white' }}>
                  {['File No', 'Year', 'Section', 'Category', 'Subject', 'Updated', 'Files', 'Actions'].map((h) => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600 }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((row: any, idx: number) => {
                  const rowBg = idx % 2 === 0 ? 'white' : 'var(--color-gray-50)';
                  const filesCount = Array.isArray(row.documents) ? row.documents.length : 0;
                  return (
                    <tr key={row.rrms_cases_draft_id} style={{ background: rowBg, borderBottom: '1px solid var(--color-gray-100)' }}>
                      <td style={{ padding: '10px 16px' }}>{row.fir_number || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>{row.year || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>{row.hierarchy_path || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>{row.category || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>{row.subject || '—'}</td>
                      <td style={{ padding: '10px 16px' }}>
                        {row.updated_at ? format(new Date(row.updated_at), 'dd MMM yyyy, HH:mm') : '—'}
                      </td>
                      <td style={{ padding: '10px 16px' }}>{filesCount}</td>
                      <td style={{ padding: '10px 16px' }}>
                        <button
                          type="button"
                          onClick={() => navigate(`${ROUTES.UPLOAD}?draft_id=${row.rrms_cases_draft_id}`)}
                          className="inline-flex items-center gap-1 rounded border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50"
                        >
                          <Eye size={12} />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Showing {(pagination.page - 1) * pagination.limit + 1}–
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={!pagination.hasPrevPage || pagination.page <= 1}
                className="px-2 py-1 rounded border border-gray-200 disabled:opacity-50"
              >
                <ChevronLeft size={14} />
              </button>
              <span className="text-sm">
                Page {pagination.page} / {pagination.totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                disabled={!pagination.hasNextPage || pagination.page >= pagination.totalPages}
                className="px-2 py-1 rounded border border-gray-200 disabled:opacity-50"
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftsPage;

